import { useState } from "react";
import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { useOverlayTransform } from "./useOverlayTransform";

const HEIGHT = 68;
const PAD_RIGHT = 20;

function RulerSvg({ pxPerCm, cmCount }) {
  const totalPx = cmCount * pxPerCm;
  const width = totalPx + PAD_RIGHT;
  const pxPerInch = pxPerCm * 2.54;

  // Inch scale — top edge (ticks hang down from y=0)
  const inchTicks = [];
  for (let inch = 0; inch * pxPerInch <= totalPx + 1; inch++) {
    const x = inch * pxPerInch;
    if (x > totalPx + 1) break;
    inchTicks.push(
      <line key={`inT${inch}`} x1={x} y1={0} x2={x} y2={18} stroke="#b71c1c" strokeWidth={2} />
    );
    if (inch > 0) {
      inchTicks.push(
        <text key={`inLbl${inch}`} x={x} y={27} fill="#b71c1c" fontSize={11} fontWeight={800} textAnchor="middle">
          {inch}
        </text>
      );
    }
    const halfX = x + pxPerInch / 2;
    if (halfX <= totalPx + 1) {
      inchTicks.push(
        <line key={`inHalf${inch}`} x1={halfX} y1={0} x2={halfX} y2={13} stroke="#c62828" strokeWidth={1.25} />
      );
    }
    for (let q of [1, 3]) {
      const qx = x + (q * pxPerInch) / 4;
      if (qx <= totalPx + 1) {
        inchTicks.push(
          <line key={`inQ${inch}-${q}`} x1={qx} y1={0} x2={qx} y2={8} stroke="#e57373" strokeWidth={1.1} />
        );
      }
    }
    for (let e of [1, 3, 5, 7]) {
      const ex = x + (e * pxPerInch) / 8;
      if (ex <= totalPx + 1) {
        inchTicks.push(
          <line key={`inE${inch}-${e}`} x1={ex} y1={0} x2={ex} y2={5} stroke="#ef9a9a" strokeWidth={0.9} />
        );
      }
    }
  }

  // CM scale — bottom edge (ticks rise up from y=HEIGHT)
  const cmTicks = [];
  for (let cm = 0; cm <= cmCount; cm++) {
    const x = cm * pxPerCm;
    const isMajorLabel = cm % 5 === 0;
    cmTicks.push(
      <line key={`cmB${cm}`} x1={x} y1={HEIGHT} x2={x} y2={HEIGHT - 18} stroke="#1b5e20" strokeWidth={2} />
    );
    cmTicks.push(
      <text
        key={`cmLbl${cm}`}
        x={x}
        y={HEIGHT - 22}
        fill="#1b5e20"
        fontSize={isMajorLabel ? 11 : 9}
        fontWeight={isMajorLabel ? 800 : 700}
        textAnchor="middle"
      >
        {cm}
      </text>
    );
    if (cm < cmCount) {
      const half = x + pxPerCm / 2;
      cmTicks.push(
        <line key={`cmHalf${cm}`} x1={half} y1={HEIGHT} x2={half} y2={HEIGHT - 12} stroke="#2e7d32" strokeWidth={1.25} />
      );
      for (let mm = 1; mm < 10; mm++) {
        if (mm === 5) continue;
        const mx = x + (mm * pxPerCm) / 10;
        cmTicks.push(
          <line key={`mm${cm}-${mm}`} x1={mx} y1={HEIGHT} x2={mx} y2={HEIGHT - 7} stroke="#43a047" strokeWidth={1.0} />
        );
      }
    }
  }

  return (
    <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} style={{ display: "block", overflow: "visible" }}>
      <rect x={0} y={0} width={width} height={HEIGHT} fill="rgba(255,255,255,0.88)" stroke="#555" strokeWidth="1.5" rx={3} />
      <line x1={0} y1={HEIGHT / 2} x2={width} y2={HEIGHT / 2} stroke="#ddd" strokeWidth={1} />
      {inchTicks}
      {cmTicks}
      <text x={width - 3} y={13} fill="#b71c1c" fontSize="9" fontWeight="800" textAnchor="end">in</text>
      <text x={width - 3} y={HEIGHT - 3} fill="#1b5e20" fontSize="9" fontWeight="800" textAnchor="end">cm</text>
    </svg>
  );
}

export default function VirtualRulerOverlay({ visible, pxPerCm = 40 }) {
  const [cmCount, setCmCount] = useState(15);
  const { transform, onPointerDownMove, onPointerDownRotate, onPointerDownLength } = useOverlayTransform();

  if (!visible) return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        left: transform.x,
        top: transform.y,
        transform: `translate(-50%, -50%) rotate(${transform.rotation}deg)`,
        transformOrigin: "center center",
        zIndex: 9999,
        pointerEvents: "auto",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.25))",
      }}
      onPointerDown={onPointerDownMove}
    >
      <RulerSvg pxPerCm={pxPerCm} cmCount={cmCount} />
      <Tooltip title="Drag to rotate (hold Shift to snap to 1°)" placement="bottom">
        <Box
          onPointerDown={onPointerDownRotate}
          sx={{
            position: "absolute",
            left: -14,
            top: HEIGHT / 2 - 14,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "#2e7d32",
            color: "white",
            fontSize: "0.7rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            border: "2px solid white",
          }}
        >
          ↻
        </Box>
      </Tooltip>
      <Tooltip title="Drag to extend/shorten" placement="bottom">
        <Box
          onPointerDown={(e) =>
            onPointerDownLength(e, {
              min: 5,
              max: 30,
              perPx: 1 / Math.max(8, pxPerCm / 3),
              current: cmCount,
              onChange: setCmCount,
            })
          }
          sx={{
            position: "absolute",
            right: -14,
            top: HEIGHT / 2 - 14,
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: "#1b5e20",
            color: "white",
            fontSize: "0.9rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ew-resize",
            border: "2px solid white",
          }}
        >
          ⇔
        </Box>
      </Tooltip>
    </Box>,
    document.body
  );
}
